import type { MetricSeriesSet, SystemDatabaseSummary } from '../../../generated/api/types.gen.js';
import { formatBytes } from '../../../utils/format-bytes.js';

/** The units the server's charts are measured in (`MetricSeriesSet.unit`). */
export type MetricUnit = MetricSeriesSet['unit'];
/** A chart range (`1h` … `1y`), as the server's series routes take it. */
export type MetricRange = MetricSeriesSet['range'];
/** A monitored database (`app`, `market`). */
export type SystemDatabaseId = SystemDatabaseSummary['id'];

// A size in bytes as people read it; shared with every surface that shows one (an attached file).
export { formatBytes };

export function formatRatio(ratio: number | null | undefined, digits = 0): string {
  if (ratio === null || ratio === undefined || !Number.isFinite(ratio)) return '—';
  return `${(ratio * 100).toFixed(digits)} %`;
}

export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(2)} B`;
  if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(1)} M`;
  if (Math.abs(value) >= 1e4) return `${(value / 1e3).toFixed(1)} K`;
  if (Number.isInteger(value)) return String(value);
  // Decimals only where they still say something: 1046/s, 61.7/s, 7.78/s.
  return value.toFixed(Math.abs(value) >= 100 ? 0 : Math.abs(value) >= 10 ? 1 : 2);
}

export function formatSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—';
  if (seconds < 1) return `${Math.round(seconds * 1000)} ms`;
  if (seconds < 120) return `${Math.round(seconds)} s`;
  if (seconds < 7_200) return `${Math.round(seconds / 60)} min`;
  if (seconds < 172_800) return `${(seconds / 3_600).toFixed(1)} h`;
  return `${Math.round(seconds / 86_400)} days`;
}

/** A value in its chart's unit, for axes, tooltips and summary numbers. */
export function formatMetric(unit: MetricUnit, value: number | null | undefined): string {
  switch (unit) {
    case 'ratio':
      return formatRatio(value);
    case 'bytes':
      return formatBytes(value);
    case 'bytes_per_second':
      return value === null || value === undefined ? '—' : `${formatBytes(value)}/s`;
    case 'per_second':
      return value === null || value === undefined ? '—' : `${formatCount(value)}/s`;
    case 'seconds':
      return formatSeconds(value);
    case 'celsius':
      return value === null || value === undefined ? '—' : `${Math.round(value)} °C`;
    case 'watts':
      return value === null || value === undefined ? '—' : `${Math.round(value)} W`;
    case 'cores':
      return value === null || value === undefined ? '—' : `${value.toFixed(value < 10 ? 2 : 1)} cores`;
    case 'count':
      return formatCount(value);
  }
}

/** A y-axis label: the same value without spaces, so it fits the axis on one line ("954MB", "44%"). */
export function formatAxisMetric(unit: MetricUnit, value: number): string {
  // The card's title names the rate, so a byte rate drops its "/s" here ("17.2MB" fits; "17.2MB/s" is clipped).
  // Cores are a plain number on the axis: the card's title says "in cores".
  if (unit === 'cores') return value.toFixed(value < 10 ? 1 : 0);
  const shown = unit === 'bytes_per_second' ? formatBytes(value) : formatMetric(unit, value);
  return shown.replace(/\s+/g, '');
}

/** An x-axis label: the time of day for short ranges, the date for long ones. */
export function formatChartTime(epochMs: number, range: MetricRange): string {
  const date = new Date(epochMs);
  if (range === '1h' || range === '6h' || range === '24h') {
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  }
  if (range === '7d') {
    return date.toLocaleString(undefined, { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export function formatUptime(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—';
  const days = Math.floor(seconds / 86_400);
  const hours = Math.floor((seconds % 86_400) / 3_600);
  const minutes = Math.floor((seconds % 3_600) / 60);
  if (days > 0) return `${days} ${days === 1 ? 'day' : 'days'}, ${hours} h`;
  if (hours > 0) return `${hours} h ${minutes} min`;
  return `${minutes} min`;
}

export function formatAgo(iso: string | null | undefined, nowMs: number): string {
  if (!iso) return 'never';
  const seconds = Math.max(0, (nowMs - new Date(iso).getTime()) / 1000);
  return seconds < 60 ? 'just now' : `${formatSeconds(seconds)} ago`;
}
